import { useCallback, useMemo } from "react";
import {
  useFilterState,
  DEFAULT_DATE_PRESETS,
  type UseFilterStateResult,
  type FilterDef,
} from "@salestrak/ui";

// The library's filter types, re-exported so app modules import them from one place.
export type {
  FilterDef,
  SelectFilterDef,
  EntityFilterDef,
  DateFilterDef,
  TableFilter,
} from "@salestrak/ui";

import { combineWhereConditions } from "#app/lib/query-utils";

// --- Extended result with Prisma where-clause support ---

// `TItem` threads through to the library's `TableFilter<TItem>[]`, so a table
// that filters Members gets Member-typed filters instead of `unknown`.
export type UseTableFiltersResult<TItem = unknown> =
  UseFilterStateResult<TItem> & {
    where: Record<string, unknown> | undefined;
    /** The where with one filter (or a set of them) left out — the per-bucket
     *  query of a grouped table, the counts a chip shows for its own options. */
    getWhereExcluding: (
      excludeKey: string | readonly string[],
    ) => Record<string, unknown> | undefined;
  };

/**
 * A filter def plus the one thing the library's cannot know: how its values
 * become a where predicate when the generic mapping below is not it — a
 * status whose one option folds three server statuses, a presence flag that
 * is not a column. Every other def leaves `toWhere` off and takes the
 * generic mapping.
 */
export type AppFilterDef<TItem = unknown> = FilterDef<TItem> & {
  toWhere?: (values: string[]) => Record<string, unknown> | undefined;
};

// --- Prisma-specific where builder (stays in consumer) ---

function buildWhere<TItem>(def: AppFilterDef<TItem>, values: string[]): Record<string, unknown> | undefined {
  if (values.length === 0) return undefined;
  if (def.toWhere) return def.toWhere(values);

  if (def.type === 'select') {
    const op = def.operator ?? "hasSome";
    const multiple = def.multiple ?? true;
    if (!multiple) return { [def.key]: { [op]: values[0] } };
    return { [def.key]: { [op]: values } };
  }

  if (def.type === "entity") {
    const op = def.operator ?? "hasSome";
    const format = def.filterFormat ?? "idArray";
    if (format === "entityRef") {
      return { [def.key]: { entityId: { [op]: values } } };
    }
    return { [def.key]: { [op]: values } };
  }

  if (def.type === "date") {
    const presets = def.presets ?? DEFAULT_DATE_PRESETS;
    const preset = presets.find((p) => p.value === values[0]);
    if (!preset) return undefined;
    const date = new Date();
    date.setDate(date.getDate() - preset.days);
    date.setHours(0, 0, 0, 0);
    return { [def.key]: { gte: date.toISOString() } };
  }

  return undefined;
}

// --- Hook ---

export function useTableFilters<TItem = unknown>(
  filterDefs: AppFilterDef<TItem>[],
): UseTableFiltersResult<TItem> {
  const filterState = useFilterState<TItem>(filterDefs);

  // Keyed by content: a def rebuilt for its pool's items (a page landing, a
  // search) maps the same values to the same where, so `where` and
  // `getWhereExcluding` keep their identity.
  const wheresKey = useMemo(() => {
    const wheres: [string, Record<string, unknown>][] = [];
    for (const def of filterDefs) {
      const w = buildWhere(def, filterState.filterValues[def.key] ?? []);
      if (w) wheres.push([def.key, w]);
    }
    return JSON.stringify(wheres);
  }, [filterDefs, filterState.filterValues]);
  const wheres = useMemo(
    () => JSON.parse(wheresKey) as [string, Record<string, unknown>][],
    [wheresKey],
  );
  const where = useMemo(
    () => combineWhereConditions(wheres.map(([, w]) => w)),
    [wheres],
  );

  const getWhereExcluding = useCallback(
    (excludeKey: string | readonly string[]) => {
      const excluded = typeof excludeKey === "string" ? [excludeKey] : excludeKey;
      return combineWhereConditions(
        wheres.filter(([key]) => !excluded.includes(key)).map(([, w]) => w),
      );
    },
    [wheres],
  );

  return useMemo(() => ({
    ...filterState,
    where,
    getWhereExcluding,
  }), [filterState, where, getWhereExcluding]);
}
